import React, { useMemo, useState } from "react";
import { Box, Button, FormControl } from "@mui/material";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import AddTransportGroupDialog from "../dialogs/AddTransportGroupDialog.jsx";
import { can } from "../../../shared utils/auth";

// The transfers page only handles arrival/departure transport.
const TRANSFER_JOURNEY_TYPES = ["ARRIVALS", "DEPARTURES"];

export default function TransfersSidebar({

  campuses = [],
  airports = [],
  transfers = [],
  
  eligibilityCtx = null,
  unassignPassengersFromGroup,
  onSaveTransportChanges,
  onDownload,
  onDownloadJourneysPdf,
  onOpenAddGroup,
  addTransportDialogOpen,
  setAddTransportDialogOpen,
  onAddTransportGroup,

  onEditTransportGroup,

  bulkGroupId,
  setBulkGroupId,
  transportGroups,

  selectedPersonKeys,
  onBulkApplyToSelected,
}) {

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editGroupId, setEditGroupId] = useState("");

  // The api/transport/groups feed also returns ACTIVITY buses, which must NOT
  // be selectable or editable here (their dialog/route model is different).
  // Scope every selector and the edit lookup to arrival/departure groups only.
  const transferGroups = useMemo(
    () =>
      (transportGroups || []).filter((g) =>
        TRANSFER_JOURNEY_TYPES.includes(
          String(g?.journeyType ?? g?.meta?.journeyType ?? "").trim().toUpperCase()
        )
      ),
    [transportGroups]
  );

  const groupById = useMemo(() => {
    const m = new Map();
    transferGroups.forEach((g) => m.set(String(g.id), g));
    return m;
  }, [transferGroups]);

  const editGroup = editGroupId ? groupById.get(String(editGroupId)) : null;

  const currentPassengers = useMemo(() => {
    if (!editGroup) return [];
    return (transfers || []).filter((t) => {
      const personType = String(t?.personType || "").toUpperCase();
      const isAdult = personType === "ADULT" || (t?.adultId && Number(t.adultId) !== 0);
      return isAdult
        ? (editGroup.adultIds || []).includes(t.adultId)
        : (editGroup.studentIds || []).includes(t.studentId);
    });
  }, [transfers, editGroup]);
  
  // Seating people on a bus, creating one and editing one all go through the
  // transport rules the server now enforces; the downloads are reads and stay.
  const canEdit = can("transport");

  return (
    <Box
      sx={{
        position: "sticky",
        top: 100,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      {canEdit && (
        <Button variant="contained" onClick={onSaveTransportChanges}>
          Save Transport Changes
        </Button>
      )}

      <Button variant="outlined" startIcon={<DownloadForOfflineIcon />} onClick={onDownload}>
        Download (XLSX)
      </Button>

      <Button variant="outlined" startIcon={<DownloadForOfflineIcon />} onClick={onDownloadJourneysPdf}>
        Journeys PDF
      </Button>

      {canEdit && (
        <Button variant="outlined" onClick={onOpenAddGroup}>
          Add Transport Group
        </Button>
      )}

      <AddTransportGroupDialog
        open={addTransportDialogOpen}
        onClose={() => setAddTransportDialogOpen(false)}
        campuses={campuses}
        servedAirports={airports}
        onCreated={(group) => {
          onAddTransportGroup(group);
          setAddTransportDialogOpen(false);
        }}
      />

      {canEdit && (
      <>
      {/* ✅ NEW: edit group picker */}
      <FormControl variant="standard" fullWidth>
        <select value={editGroupId} onChange={(e) => setEditGroupId(e.target.value)}>
          <option value="">Edit group…</option>
          {transferGroups.map((g) => (
            <option key={g.id} value={String(g.id)}>
              {g.groupName}
            </option>
          ))}
        </select>
      </FormControl>

      <Button
        variant="outlined"
        disabled={!editGroupId}
        onClick={() => setEditDialogOpen(true)}
      >
        Edit Selected Group
      </Button>

      {/* ✅ Reuse the same dialog in edit mode */}
      <AddTransportGroupDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        campuses={campuses}
        servedAirports={airports}
        mode="edit"
        initialGroup={editGroup}
        currentPassengers={currentPassengers}
        eligibilityCtx={eligibilityCtx}
        onUpdated={async (groupId, patch) => {
          try {
            const res = await onEditTransportGroup(groupId, patch);
            if (res?.ok) setEditDialogOpen(false);
          } catch (e) {
            console.error(e);
          }
        }}

        onUnassignMismatched={async (passengers) => {
          if (!editGroup?.id) return;
          await unassignPassengersFromGroup(editGroup.id, passengers);
        }}
      />

      <FormControl variant="standard" fullWidth>
        <select value={bulkGroupId} onChange={(e) => setBulkGroupId(e.target.value)}>
          <option value="">None</option>
          {transferGroups.map((g) => (
            <option key={g.id} value={String(g.id)}>
              {g.groupName}
            </option>
          ))}
        </select>
      </FormControl>

      <Button
        variant="outlined"
        disabled={selectedPersonKeys.length === 0}
        onClick={onBulkApplyToSelected}
      >
        Apply to Selected ({selectedPersonKeys.length})
      </Button>
      </>
      )}
    </Box>
  );
}
